import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import * as myConstClass from './constant.js';
import { getLanguage } from 'react-switch-lang';
import { toast } from 'react-toastify';
import { getCartDisplayCount } from './constant.js';
import { updateGuestCartDisplay } from './utils/guestCartHelpers';
import { buildDeepLink, shareDeepLink } from './utils/deepLink';
import shareIcon from './assets/icons/share.svg';
import './MachineTestWidget.css';

export default function MachineTestWidget({ schedules = [], eventInfo = {}, onAddToCart, machineTestsInCart = {} }) {
  const history = useHistory();
  const [selectedCounts, setSelectedCounts] = useState({});
  const [imagesMap, setImagesMap] = useState({});
  const [currentIndexMap, setCurrentIndexMap] = useState({});
  const [subtopics, setSubtopics] = useState([]);

  const handleCountChange = (scheduleId, field, value) => {
    setSelectedCounts(prev => ({ ...prev, [scheduleId]: { ...(prev[scheduleId] || {}), [field]: parseInt(value || 0) } }));
  };

  const handleShare = async (schedule, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      const origin = window && window.location && window.location.origin ? window.location.origin : '';
      const eventId = schedule.event_id || schedule.schedule_id || schedule.scheduleId;
      const title = schedule.title || eventInfo?.title || 'Machine Test';
      
      // Build minimal deep link with only event ID (no title, date, or location)
      const deepLink = buildDeepLink({ 
        origin, 
        eventId
      });
      
      await shareDeepLink({ 
        title: title || 'Machine Test', 
        text: '', 
        url: deepLink 
      });
    } catch (err) {
      console.error('Share failed', err);
      toast.error('Failed to share link');
    }
  };

  const addToCart = async (schedule) => {
    const eventId = schedule.event_id || schedule.schedule_id || schedule.scheduleId;
    const counts = selectedCounts[eventId] || {};
    const parent = counts.parent || 1;
    const child = counts.child || 0;

    // If onAddToCart prop is provided, use it (for EventDetailsPage with modal)
    if (onAddToCart) {
      onAddToCart(eventId, parent, child, schedule);
      return;
    }

    // Otherwise, add directly to cart (legacy behavior)
    const user_id = localStorage.getItem('user_id') || 0;

    if (user_id && user_id > 0) {
      try {
        const url = myConstClass.BASE_URL + `cart/addMachineTestEventToCart/${eventId}/${user_id}/${parent}/${child}`;
        await fetch(url, { method: 'GET' });
        // Refresh header cart count using the fixed getCartDisplayCount
        getCartDisplayCount(user_id).then(count => {
          document.querySelectorAll('.lblCartCount').forEach(el => {
            el.textContent = count;
          });
        }).catch(err => console.error('Error updating cart count:', err));
        toast('Machine test added in cart', { containerId: 'B', type: toast.TYPE.SUCCESS });
      } catch (err) {
        console.error(err);
        toast('Error adding to cart', { containerId: 'B', type: toast.TYPE.ERROR });
      }
    } else {
      // guest: save to localStorage guest_mt_cart (machine test cart)
      try {
        const guestMtCart = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
        const exists = guestMtCart.find(g => g.event_id == eventId);
        const item = {
          event_id: eventId,
          parent: parent,
          child: child,
          title: eventInfo.title || schedule.title || 'Machine Test',
          price: eventInfo.first_price || schedule.first_price || '0',
          first_price: eventInfo.first_price || schedule.first_price || '0',
          second_price: schedule.second_price || '0',
          child_price: schedule.child_price || '0',
          first_price_for: eventInfo.first_price_for || 'Person',
          event_type: 'Machine Test',
          event_type_id: 3,
          added_at: new Date().toISOString()
        };
        if (!exists) guestMtCart.push(item); else {
          exists.parent = parent; exists.child = child;
        }
        localStorage.setItem('guest_mt_cart', JSON.stringify(guestMtCart));
        updateGuestCartDisplay();
        toast('Machine test added in cart. Login to persist.', { containerId: 'B', type: toast.TYPE.SUCCESS });
      } catch (err) {
        console.error(err);
        toast('Error saving to cart', { containerId: 'B', type: toast.TYPE.ERROR });
      }
    }
  };

  // image removed: Machine test widget doesn't show schedule images
  // Fetch images for a given event id and store in imagesMap
  const fetchImagesForEvent = async (eventId, key) => {
    if (!eventId) return;
    try {
      const url = myConstClass.NODE_API_BASE + `/event/media/getImages/${eventId}/en`;
      const res = await fetch(url);
      const data = await res.json();
      // Data may be { status: 'true', data: [...] } or an array
      const imgs = Array.isArray(data) ? data : (data?.data || []);
      const urls = imgs.map(i => i.image_url || i.imageUrl || i.image_name || i.url).filter(Boolean);
      setImagesMap(prev => ({ ...prev, [key]: urls }));
      setCurrentIndexMap(prev => ({ ...prev, [key]: 0 }));
    } catch (err) {
      console.error('Error fetching images for event', eventId, err);
      setImagesMap(prev => ({ ...prev, [key]: [] }));
    }
  };

  useEffect(() => {
    // Fetch images for each schedule when schedules prop changes
    schedules.forEach((s) => {
      const eventId = s.event_id || s.schedule_id || s.scheduleId || eventInfo.event_id || eventInfo.eventId;
      const key = s.schedule_id || s.event_id || s.scheduleId || String(eventId);
      if (!imagesMap[key]) {
        fetchImagesForEvent(eventId, key);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules]);

  // Initialize selectedCounts for schedules so parent quantity defaults to 1
  useEffect(() => {
    if (!Array.isArray(schedules) || schedules.length === 0) return;
    setSelectedCounts(prev => {
      const next = { ...prev };
      schedules.forEach(s => {
        const key = s.event_id || s.schedule_id || s.scheduleId;
        if (key === undefined || key === null) return;
        if (!next[key]) {
          next[key] = { parent: 1, child: 0 };
        } else {
          if (typeof next[key].parent === 'undefined') next[key].parent = 1;
          if (typeof next[key].child === 'undefined') next[key].child = 0;
        }
      });
      return next;
    });
  }, [schedules]);

  // Fetch subtopics for the event (use eventInfo.topic_id if available)
  useEffect(() => {
    // First check if subtopics are already in eventInfo (from Node API response)
    if (eventInfo?.subtopics && Array.isArray(eventInfo.subtopics) && eventInfo.subtopics.length > 0) {
      // Subtopics already available in eventInfo - extract titles
      const currentLanguage = getLanguage();
      const isMarathi = currentLanguage === 'ma';
      const subtopicTitles = eventInfo.subtopics.map(st => 
        isMarathi ? (st.titleMr || st.subtopic_title_mr || st.title || st.subtopic_title) 
                  : (st.titleEn || st.subtopic_title_en || st.title || st.subtopic_title)
      ).filter(Boolean);
      setSubtopics(subtopicTitles.map((title, idx) => ({ 
        subtopic_title: title,
        id: eventInfo.subtopics[idx]?.id || idx 
      })));
      return;
    }

    const topicId = eventInfo?.topic_id || eventInfo?.topicId || eventInfo?.topicIdFk || eventInfo?.topic_id_value || null;
    if (!topicId) {
      setSubtopics([]);
      return;
    }

    let cancelled = false;
    const lang = getLanguage() || 'en';
    // Use Node API endpoint instead of PHP API
    const url = myConstClass.NODE_API_BASE + `/event/subtopics/${topicId}?language=${lang}`;

    fetch(url)
      .then(res => {
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        // Handle Node API response format
        const list = data?.status === 'true' && data?.data ? data.data : (Array.isArray(data) ? data : []);
        setSubtopics(list || []);
      })
      .catch(err => {
        if (!cancelled) setSubtopics([]);
      });

    return () => { cancelled = true; };
  }, [eventInfo]);

  const showPrev = (key) => {
    setCurrentIndexMap(prev => {
      const cur = prev[key] || 0;
      const imgs = imagesMap[key] || [];
      const next = (cur - 1 + imgs.length) % Math.max(1, imgs.length);
      return { ...prev, [key]: next };
    });
  };

  const showNext = (key) => {
    setCurrentIndexMap(prev => {
      const cur = prev[key] || 0;
      const imgs = imagesMap[key] || [];
      const next = (cur + 1) % Math.max(1, imgs.length);
      return { ...prev, [key]: next };
    });
  };

  return (
    <div className="machine-test-widget">
      {schedules.map((s) => (
        <div key={s.schedule_id || s.event_id || s.scheduleId} className="event-schedule-card event-card" style={{ padding: 0, position: 'relative' }}>
          <button
            type="button"
            onClick={(e) => handleShare(s, e)}
            aria-label="Share machine test"
            title="Share"
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'transparent',
              border: 'none',
              borderRadius: 0,
              padding: '6px',
              cursor: 'pointer',
              zIndex: 10,
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img src={shareIcon} alt="Share" style={{ width: 18, height: 18 }} />
          </button>
          <div className="card mt-card">
            <div className="mt-card-inner">
              {/* Left: image slider - fixed visual area to contain full image and arrows */}
              <div className="mt-image">
                {(() => {
                  const key = s.schedule_id || s.event_id || s.scheduleId || String(s.event_id || s.schedule_id);
                  const imgs = imagesMap[key] || [];
                  const cur = currentIndexMap[key] || 0;
                  if (imgs.length === 0) {
                    return (
                      <div className="mt-image-placeholder">
                        <span>Images will be available soon.</span>
                      </div>
                    );
                  }

                  return (
                    <div className="mt-image-inner">
                      <img src={imgs[cur]} alt={`Event image ${cur+1}`} />
                      {imgs.length > 1 && (
                        <>
                          <button className="mt-arrow mt-prev" onClick={()=>showPrev(key)} aria-label="Previous"><i className="fas fa-chevron-left" aria-hidden="true"></i></button>
                          <button className="mt-arrow mt-next" onClick={()=>showNext(key)} aria-label="Next"><i className="fas fa-chevron-right" aria-hidden="true"></i></button>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Right: info and controls (all other details go here) */}
              <div className="mt-info">
                <div>
                  <h5>{s.title}</h5>
                  <p className="mt-info-date">{s.start_date_format || s.date || ''}</p>

                  {/* Subtopics (render as pills) */}
                  {subtopics && subtopics.length > 0 && (
                    <div className="mt-subtopics" style={{ marginTop: 6 }}>
                      {subtopics.map((st, idx) => {
                        // st may be a string or an object. Normalize
                        const isObj = typeof st === 'object' && st !== null;
                        const subtopicId = isObj ? (st.id || st.subtopic_id || st.subtopicId) : null;
                        const subtopicTitle = isObj
                          ? (st.titleEn || st.titleMr || st.subtopic_title || st.name || st.title)
                          : String(st);

                        const handleClick = (e) => {
                          if (!subtopicId) return;
                          e.preventDefault();
                          e.stopPropagation();

                          // Determine topic label and id from eventInfo
                          const topicObj = Array.isArray(eventInfo?.topics) && eventInfo.topics.length > 0
                            ? eventInfo.topics[0]
                            : null;

                          const topicId = topicObj?.id || eventInfo?.topic_id || eventInfo?.topicId || null;
                          const topicLabel = topicObj?.titleEn || topicObj?.title || eventInfo?.topic || eventInfo?.topicName || eventInfo?.title || 'Topics';

                          const pathname = topicId
                            ? `/topics/${encodeURIComponent(topicLabel)}/${topicId}/${subtopicId}`
                            : `/topics/${encodeURIComponent(topicLabel)}/${subtopicId}`;

                          history.push({
                            pathname,
                            state: {
                              selectedTopic: topicLabel,
                              selectedSubtopic: subtopicTitle,
                              subtopicId: subtopicId
                            }
                          });
                        };

                        return (
                          <span
                            key={idx}
                            className={`mt-subtopic-pill ${subtopicId ? 'clickable' : ''}`}
                            style={{ marginRight: 6, marginBottom: 6, display: 'inline-block', cursor: subtopicId ? 'pointer' : 'default' }}
                            role={subtopicId ? 'button' : undefined}
                            tabIndex={subtopicId ? 0 : undefined}
                            onClick={subtopicId ? handleClick : undefined}
                            onKeyPress={subtopicId ? (e => { if (e.key === 'Enter' || e.key === ' ') { handleClick(e); } }) : undefined}
                          >
                            {subtopicTitle}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Language */}
                  {(s.language || s.language_name || eventInfo?.arr_event_languages?.[0]?.label) && (
                    <p className="mt-info-language">
                      <strong>Language:</strong> {s.language || s.language_name || eventInfo?.arr_event_languages?.[0]?.label}
                    </p>
                  )}
                  
                  {/* Age Group */}
                  {(eventInfo.age_group || s.age_group) && (
                    <p className="mt-info-age-group">
                      <strong>Age Group:</strong> {eventInfo.age_group || s.age_group}
                    </p>
                  )}
                  
                  {/* Reviews and Likes */}
                  {(eventInfo.total_review || eventInfo.total_like) && (
                    <p className="mt-info-reviews">
                      {eventInfo.total_review > 0 && (
                        <span className="mr-2">
                          <i className="far fa-comment-dots" style={{color: '#ff8c00'}}></i> 
                          <span className="small"> {eventInfo.total_review}</span>
                        </span>
                      )}
                      {eventInfo.total_like > 0 && (
                        <span>
                          <i className="fas fa-thumbs-up" style={{color: '#ff8c00'}}></i> 
                          <span className="small"> {eventInfo.total_like}</span>
                        </span>
                      )}
                    </p>
                  )}
                  
                  <div className="mt-price-wrapper">
                    <strong className="mt-price-main">INR {eventInfo.first_price || s.first_price || '0'}/-</strong>
                    {(eventInfo.first_price_for || s.first_price_for) && (
                      <span className="mt-price-for">{((eventInfo.first_price_for || s.first_price_for) && String(eventInfo.first_price_for || s.first_price_for).toLowerCase().includes('per')) ? (eventInfo.first_price_for || s.first_price_for) : ` per ${eventInfo.first_price_for || s.first_price_for}`}</span>
                    )}
                    { (eventInfo.second_price || s.second_price || eventInfo.second_price_for || s.second_price_for) ? (
                      <span className="mt-price-label">{eventInfo.second_price_for || s.second_price_for || ''}</span>
                    ) : null }
                  </div>
                </div>

                <div className="mt-controls-wrapper">
                  <div className="mt-selectors">
                    <div className="mt-selector-group">
                      <label className="small">Quantity</label>
                      <select
                        className="form-control form-control-sm"
                        value={(selectedCounts[s.event_id || s.schedule_id || s.scheduleId] && typeof selectedCounts[s.event_id || s.schedule_id || s.scheduleId].parent !== 'undefined') ? selectedCounts[s.event_id || s.schedule_id || s.scheduleId].parent : 1}
                        onChange={(e)=>handleCountChange(s.event_id || s.schedule_id || s.scheduleId,'parent', e.target.value)}
                      >
                        {[0,1,2,3,4,5,6,7,8,9,10].map(n=> <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    { (eventInfo.second_price || s.second_price || eventInfo.second_price_for || s.second_price_for) ? (
                        <div className="mt-selector-group">
                        <label className="small">Quantity</label>
                        <select
                          className="form-control form-control-sm"
                          value={(selectedCounts[s.event_id || s.schedule_id || s.scheduleId] && typeof selectedCounts[s.event_id || s.schedule_id || s.scheduleId].child !== 'undefined') ? selectedCounts[s.event_id || s.schedule_id || s.scheduleId].child : 0}
                          onChange={(e)=>handleCountChange(s.event_id || s.schedule_id || s.scheduleId,'child', e.target.value)}
                        >
                          {[0,0,1,2,3,4].map((n,i)=> <option key={i} value={n}>{n}</option>)}
                        </select>
                      </div>
                    ) : null }
                  </div>

                  <div className="mt-action-wrapper">
                    <button className="btn btn-brown btn-sm" onClick={()=>addToCart(s)}>
                      {machineTestsInCart[s.event_id || s.schedule_id || s.scheduleId] ? 'ADD MORE' : 'ADD TO CART'}
                    </button>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        
        </div>
      ))}
    </div>
    );

}
